import React, { useCallback, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { PromiseState } from 'react-refetch';

import Box from 'components/elements/Box';
import P from 'components/elements/p';
import SearchField from 'components/styled/SearchField/SearchField';
import { NoResults } from 'components/utility/NoResults/NoResults';
import AddToListButton from 'components/base/AddToListButton';
import { ResultItemWrapper } from 'components/search/ResultsSection/ResultsList/ResultsList';
import SearchResultsLoader from 'components/utility/ContentLoader/SearchResultsLoader/SearchResultsLoader';

import { ReactComponent as IconSearch } from 'icons/icon-search.svg';

import colors from 'styles/themes/colors';

import {
  CustomerNameElement,
  Label,
  PlaylistName,
  PlaylistOwner,
  ScrollBox,
  SearchDropdownBox,
} from '../../styles';
import { ICustomer } from 'redux/reducers/customers';
import CustomerIcon from 'components/base/CustomerIcon/CustomerIcon';
import getCustomerData from '../../helpers/getCustomerData';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';
import DspPlaylistFilter from 'components/base/DSPFilter/DspPlaylistFilter';

const InitialView = (): JSX.Element => (
  <Box my="4rem" textAlign="center" color={colors.MediumGrey}>
    <IconSearch />
    <P fontSize="1.25rem">Monitor Playlist Activity</P>
    <P fontSize="0.875rem">Search any playlist to monitor playlist activity</P>
  </Box>
);

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (): void => {};

const PlaylistList: React.FC<{
  foundPlaylists: any;
  addedPlaylists: string[];
  addPlaylistToUserList: any;
  isSmallScreen: boolean;
  customers?: ICustomer[];
  addingPlaylist?: string;
  removingPlaylist?: string;
  deletePlaylist?: (playlistId: string) => void;
}> = ({
  foundPlaylists,
  addedPlaylists,
  addPlaylistToUserList,
  customers,
  addingPlaylist,
  removingPlaylist,
  deletePlaylist,
}) => {
  const watchPlaylist = (playlist: any, isAdded?: boolean): void => {
    if (isAdded) {
      deletePlaylist && deletePlaylist(playlist.playlistId);
    } else {
      addPlaylistToUserList(
        playlist.playlistId,
        playlist.displayName,
        playlist.customerId
      );
    }
    logToAmplitude(
      'Playlist Activity -> Add A Playlist -> Watch button clicked'
    );
  };

  return (
    <ScrollBox
      display="flex"
      flexDirection="column"
      overflowY="scroll"
      overflowX="hidden"
      maxHeight="404px"
    >
      {foundPlaylists?.map((playlist: any, index: number) => {
        const isAdded = !!addedPlaylists?.find(
          (item: string) => playlist.playlistId === item
        );

        const { customerId, customerName } = getCustomerData(
          customers,
          playlist.customerId
        );

        const playlistAdding = addingPlaylist === playlist.playlistId;
        const playlistRemoving = removingPlaylist === playlist.playlistId;

        return (
          <ResultItemWrapper
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            key={index}
            padding="0.578704em 0px"
          >
            <Box display="flex">
              <Box
                display="flex"
                marginRight="10px"
                flexDirection="column"
                alignItems="center"
                width="60px"
              >
                <CustomerIcon showHiddenPlaceholder customer={customerId} />
                <CustomerNameElement>{customerName}</CustomerNameElement>
              </Box>
              <Box
                flexDirection="column"
                display="flex"
                alignItems="flex-start"
                justifyContent="center"
              >
                <PlaylistName>{playlist.displayName}</PlaylistName>
                <PlaylistOwner>Owned by {playlist.ownerId}</PlaylistOwner>
              </Box>
            </Box>
            <Box marginRight="4px">
              <AddToListButton
                onClick={
                  !playlistAdding && !playlistRemoving
                    ? (): void => watchPlaylist(playlist, isAdded)
                    : noop
                }
                icon={isAdded ? 'check' : 'plus'}
                loading={playlistAdding || playlistRemoving}
                forward={!isAdded}
              />
            </Box>
          </ResultItemWrapper>
        );
      })}
    </ScrollBox>
  );
};

type PropsType = {
  addPlaylistToUserList: (playlistId: string) => void;
  deletePlaylist?: (playlistId: string) => void;
  isSmallScreen: boolean;
  setSearchQuery: Function;
  foundPlaylists: PromiseState<any>;
  addedPlaylists: string[];
  isSeparate?: boolean;
  customers: ICustomer[];
  addingPlaylist?: string;
  removingPlaylist?: string;
  setDspFilter: (dspList: string[]) => void;
};

const SearchDropdown: React.FC<PropsType> = ({
  addPlaylistToUserList,
  deletePlaylist,
  isSmallScreen,
  setSearchQuery,
  foundPlaylists,
  addedPlaylists,
  isSeparate,
  customers,
  addingPlaylist,
  removingPlaylist,
  setDspFilter,
}) => {
  const { fulfilled, pending, value } = foundPlaylists;

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const hasNoSearchResults =
    searchInputValue &&
    fulfilled &&
    value &&
    value.playlists &&
    value.playlists.length === 0;
  const searchRef = useRef<HTMLInputElement>(null);

  const doSearchQuery = useCallback(
    debounce((str: string) => setSearchQuery(str), 250),
    []
  );

  const handleSearchInput = useCallback(
    (event) => {
      const value = event.target.value;
      setSearchInputValue(value);
      doSearchQuery(value);
    },
    [doSearchQuery]
  );

  return (
    <SearchDropdownBox
      width={isSmallScreen ? '100%' : '560px'}
      padding={isSmallScreen ? '0 0.75rem' : 5}
      isSeparate={isSeparate}
      isSmallScreen={isSmallScreen}
      id="playlistSearchDropdown"
    >
      <Box>
        <SearchField
          refObj={searchRef}
          handleSearch={handleSearchInput}
          value={searchInputValue}
          placeholder="Search a playlist"
          focusOnMount
        >
          <DspPlaylistFilter
            customers={customers}
            setDspFilter={setDspFilter}
          />
        </SearchField>
      </Box>

      {pending && searchInputValue && <SearchResultsLoader />}

      <>
        {fulfilled &&
          searchInputValue &&
          value &&
          value?.playlists &&
          value?.playlists?.length > 0 && (
            <Box>
              <Box display="flex" justifyContent="space-between">
                <Label>Playlists</Label>
                <Label>Watch</Label>
              </Box>

              <PlaylistList
                foundPlaylists={foundPlaylists?.value?.playlists}
                addedPlaylists={addedPlaylists}
                addPlaylistToUserList={addPlaylistToUserList}
                customers={customers}
                addingPlaylist={addingPlaylist}
                removingPlaylist={removingPlaylist}
                isSmallScreen={isSmallScreen}
                deletePlaylist={deletePlaylist}
              />
            </Box>
          )}
        {!searchInputValue && <InitialView />}

        {hasNoSearchResults && <NoResults searchValue={searchInputValue} />}
      </>
    </SearchDropdownBox>
  );
};

export default SearchDropdown;
